"use client";

import { api } from "~/trpc/react";
import type { RouterOutput } from "~/server/api/root";
import { useSession } from "next-auth/react";

export interface OnboardingStatus {
  isLoading: boolean;
  hasTherapyRelationship: boolean;
  hasCompleteProfile: boolean;
  isFullyOnboarded: boolean;
  therapyRelationships: RouterOutput["patientRouter"]["getTherapyRelationships"];
}

export function useOnboardingStatus(): OnboardingStatus {
  const { status, data: sessionData } = useSession();

  const isLoggedIn = status === "authenticated";
  const userRole = sessionData?.user.role;
  const isTherapist = userRole === "THERAPIST";
  const isPatientLikeRole = userRole === "PATIENT";

  // Always call hooks in the same order.
  // staleTime: 0 because these gate the onboarding flow: a user returning
  // from their first booking must see the relationship the payment webhook
  // just created, not the pre-booking cached empty result.
  const user = api.user.getCurrentUser.useQuery(undefined, {
    enabled: isLoggedIn && isPatientLikeRole,
    staleTime: 0,
  });
  const therapyRelationships =
    api.patientRouter.getTherapyRelationships.useQuery(undefined, {
      enabled: isLoggedIn && isPatientLikeRole,
      staleTime: 0,
    });
  const therapist = api.therapistRouter.getCurrentTherapist.useQuery(undefined, {
    enabled: isLoggedIn && isTherapist,
  });

  // For therapists, check if they have a therapist profile to determine onboarding status
  if (isTherapist) {
    // If still loading therapist data, show loading
    if (therapist.isLoading) {
      return {
        isLoading: true,
        hasTherapyRelationship: true,
        hasCompleteProfile: false,
        isFullyOnboarded: false,
        therapyRelationships: [],
      };
    }

    // If therapist doesn't exist, they need onboarding
    const hasTherapistProfile = !!therapist.data;

    return {
      isLoading: false,
      hasTherapyRelationship: true,
      hasCompleteProfile: hasTherapistProfile,
      isFullyOnboarded: hasTherapistProfile,
      therapyRelationships: [],
    };
  }

  if (userRole === "SUPER_ADMIN") {
    return {
      isLoading: false,
      hasTherapyRelationship: true,
      hasCompleteProfile: true,
      isFullyOnboarded: true,
      therapyRelationships: [],
    };
  }

  // Patient-specific logic (original logic)
  const isLoading = user.isLoading || therapyRelationships.isLoading;

  const hasTherapyRelationship =
    therapyRelationships.data && therapyRelationships.data.length > 0;

  // Check if user has complete profile using the same logic
  const profileLength =
    (user.data?.name?.length ?? 0) +
    (user.data?.address?.city?.length ?? 0) +
    (user.data?.address?.country?.length ?? 0) +
    (user.data?.patient?.bio?.length ?? 0);

  const hasCompleteProfile = profileLength > 2;

  // Every patient has Tijana available regardless of whether they have
  // picked a human therapist yet, so a completed profile is enough to enter
  // the app. This mirrors the server-side gate in
  // src/server/services/onboarding-status.ts. hasTherapyRelationship is
  // still exposed for callers unrelated to onboarding gating (e.g. the
  // booking cart indicator).
  const isFullyOnboarded = hasCompleteProfile;

  return {
    isLoading,
    hasTherapyRelationship: !!hasTherapyRelationship,
    hasCompleteProfile,
    isFullyOnboarded: !!isFullyOnboarded,
    therapyRelationships: therapyRelationships.data ?? [],
  };
}
